import { Feature, FeatureCollection } from 'geojson';
import { featureCollection } from '@turf/turf';
import { capitalize, lowerCase, uniqBy } from 'lodash';
import * as yup from 'yup';
import {
  csvFormatVariableList,
  formatSoilName,
  formatVariableList,
  handleInvalidData,
  simpleSectionSchema,
  transformArrayToObject,
  transformRawSection,
  variableListSchema,
  WrappedColumn,
} from '../../utils/soils';
import {
  RawSection,
  Section,
  VariableList,
  VarListEntry,
} from '../../types/soils';

const MU_SETTING_TITLE = 'Map Unit Setting';
const MU_COMP_TITLE = 'Map Unit Composition';
const MINOR_COMPONENTS_TITLE = 'Minor Components';
const SETTING_TITLE = 'Setting';
const TYP_PROF_TITLE = 'Typical profile';
const PROP_QUAL_TITLE = 'Properties and qualities';
const INTERP_GRP_TITLE = 'Interpretive groups';

interface TransformedMapUnitDescription {
  composition: Nullable<Section>;
  majorComponents: Section[];
  minorComponents: Nullable<Section>;
  setting: Nullable<Section>;
}

// There are common, but optional items: Map Unit Setting, Map Unit Composition,
// and Minor Components. There may also be zero or more major components.
const transformMapUnitDescriptionArray = (array: Section[]) =>
  array.reduce(
    (acc: TransformedMapUnitDescription, s: Section) => {
      switch (s.title) {
        case MU_SETTING_TITLE:
          return { ...acc, setting: s };
        case MU_COMP_TITLE:
          return { ...acc, composition: s };
        case MINOR_COMPONENTS_TITLE:
          return { ...acc, minorComponents: s };
        default:
          return { ...acc, majorComponents: [...acc.majorComponents, s] };
      }
    },
    {
      composition: null,
      majorComponents: [],
      minorComponents: null,
      setting: null,
    },
  );

const optionalSectionSchema = yup
  .object()
  .notRequired()
  .default(undefined)
  .shape({
    title: yup.string(),
    variablelist: variableListSchema,
  });

const mapUnitDescriptionSchema = yup.object().shape({
  section: yup
    .object()
    .transform(transformArrayToObject)
    .shape({
      0: yup.object().shape({
        title: yup.string().required(),
        section: yup
          .object()
          .transform(transformArrayToObject)
          .shape({
            0: yup.object().shape({
              title: yup.string().required(),
              section: yup
                .object()
                .transform(transformMapUnitDescriptionArray)
                .shape({
                  composition: optionalSectionSchema,
                  setting: optionalSectionSchema,
                  majorComponents: yup.array().of(
                    yup.object().shape({
                      title: yup.string().required(),
                      section: yup
                        .array()
                        .of(
                          yup.object().shape({
                            title: yup.string(),
                            variablelist: variableListSchema,
                          }),
                        )
                        .test({
                          test: (sections) =>
                            [
                              SETTING_TITLE,
                              TYP_PROF_TITLE,
                              PROP_QUAL_TITLE,
                              INTERP_GRP_TITLE,
                            ].some((title) =>
                              sections?.some((s) => s.title === title),
                            ),
                          message: 'Invalid major component',
                        }),
                    }),
                  ),
                  minorComponents: yup
                    .object()
                    .notRequired()
                    .default(undefined)
                    .shape({
                      title: yup.string(),
                      section: yup.array().of(simpleSectionSchema),
                    }),
                }),
            }),
          }),
      }),
    }),
});

const getValidSections = (
  data: FeatureCollection,
  accessor: (f: Feature) => Nullable<RawSection>,
  schema: yup.Schema,
): Section[] =>
  data.features.flatMap((f: Feature) => {
    const rawSection = accessor(f);
    if (rawSection == null) return [];
    try {
      const section = transformRawSection(rawSection);
      schema.validateSync(section);
      return [section];
    } catch (err) {
      handleInvalidData('Map Unit Description', rawSection, err);
      return [];
    }
  });

const transformTerm = (term: string) => {
  const displayString = term.split(':')[0];
  const words = displayString
    .split(' ')
    .map((w, i) => (i === 0 ? lowerCase(w) : capitalize(w)));
  words[0] = lowerCase(words[0]);
  return { displayString, accessor: words.join('') };
};

const getMinorComponentCols = (data: FeatureCollection) => {
  const validFeatures = getValidSections(
    data,
    (f) => f.properties?.description,
    mapUnitDescriptionSchema,
  );
  const cols = validFeatures.flatMap((s) => {
    const { section = [] } = s?.section[0]?.section[0] || {};
    const { section: minorComponents = [] } = section[3] || {};
    return minorComponents.flatMap((minorComponent) =>
      minorComponent.variablelist.flatMap((vl) =>
        vl.varlistentry.map((vle: VarListEntry) => ({
          ...transformTerm(vle.term),
          hideIfAllMatchFn: (value: never) => !value,
        })),
      ),
    );
  });
  return uniqBy(cols, 'accessor');
};

const formatSoilComponent = (
  component: string,
  mapUnitComposition: Nullable<VariableList>,
) => {
  const componentName = component.replace('Description of ', '');
  const componentMatch = mapUnitComposition?.varlistentry?.find((c) =>
    lowerCase(c.term).includes(lowerCase(componentName)),
  );
  if (!componentMatch) return componentName;
  const percent = componentMatch.listitem.para?.split(' ')?.[0];
  if (!percent) return componentName;
  return `${componentName} (${percent}%)`;
};

export const MAP_UNIT_DESCRIPTION = {
  key: 'Map Unit Description',
  name: 'Map Unit Description',
  reportId: '666',
  cols: [
    {
      displayString: 'Map unit',
      accessor: 'name',
      style: WrappedColumn,
      format: formatSoilName,
    },
    {
      displayString: 'Major component',
      accessor: 'component',
      style: WrappedColumn,
    },
    {
      displayString: 'Map unit setting',
      accessor: 'mapUnitSetting',
      style: { ...WrappedColumn, minWidth: '130px' },
      csvFormat: csvFormatVariableList,
      format: formatVariableList,
    },
    // Leaving definitions in case they become useful later
    // {
    //   displayString: 'Map unit composition',
    //   accessor: 'mapUnitComposition',
    //   style: WrappedColumn,
    //   csvFormat: csvFormatVariableList,
    //   format: formatVariableList,
    // },
    // {
    //   displayString: 'Setting',
    //   accessor: 'setting',
    //   style: WrappedColumn,
    //   csvFormat: csvFormatVariableList,
    //   format: formatVariableList,
    // },
    {
      displayString: 'Typical profile',
      accessor: 'typicalProfile',
      style: WrappedColumn,
      csvFormat: csvFormatVariableList,
      format: formatVariableList,
    },
    {
      displayString: 'Properties and qualities',
      accessor: 'propertiesAndQualities',
      style: { ...WrappedColumn, minWidth: '225px' },
      csvFormat: csvFormatVariableList,
      format: formatVariableList,
    },
    {
      displayString: 'Interpretive groups',
      accessor: 'interpretiveGroups',
      style: { ...WrappedColumn, minWidth: '225px' },
      csvFormat: csvFormatVariableList,
      format: formatVariableList,
    },
  ],
  getItems: ({ data }: { data: FeatureCollection }) => {
    const filteredFeatures = featureCollection(
      uniqBy(data.features, 'properties.mapUnitName'),
    );
    return getValidSections(
      filteredFeatures,
      (f) => f.properties?.description,
      mapUnitDescriptionSchema,
    ).flatMap((s) => {
      const { title: name, section = [] } = s.section[0]?.section[0] || {};
      const {
        composition,
        majorComponents,
        setting,
      } = transformMapUnitDescriptionArray(section);
      const { variablelist: mapUnitComposition = [] } = composition || {};

      // If there are no major components, try to parse composition info
      if (!majorComponents.length && mapUnitComposition[0]?.varlistentry?.[0]) {
        const title = mapUnitComposition[0].varlistentry[0].term.split(':')[0];
        const component = formatSoilComponent(title, mapUnitComposition[0]);
        return [{ key: `${name}:${component}`, name, component }];
      }

      return majorComponents.map((majorComponent) => {
        const { section: soilDescription = [], title = '' } = majorComponent;
        const component = formatSoilComponent(title, mapUnitComposition[0]);
        return {
          key: `${name}:${component}`,
          name,
          component,
          mapUnitSetting: setting?.variablelist,
          mapUnitComposition,
          setting: soilDescription?.find((d) => d.title === SETTING_TITLE)
            ?.variablelist,
          typicalProfile: soilDescription?.find(
            (d) => d.title === TYP_PROF_TITLE,
          )?.variablelist,
          propertiesAndQualities: soilDescription?.find(
            (d) => d.title === PROP_QUAL_TITLE,
          )?.variablelist,
          interpretiveGroups: soilDescription?.find(
            (d) => d.title === INTERP_GRP_TITLE,
          )?.variablelist,
        };
      });
    });
  },
};

export const MINOR_COMPONENTS = {
  key: MINOR_COMPONENTS_TITLE,
  name: MINOR_COMPONENTS_TITLE,
  reportId: '666',
  cols: (data: FeatureCollection) => [
    {
      displayString: 'Map unit',
      accessor: 'name',
      style: WrappedColumn,
      format: formatSoilName,
    },
    {
      displayString: 'Minor component',
      accessor: 'minorComponent',
      style: WrappedColumn,
    },
    ...getMinorComponentCols(data),
  ],
  getItems: ({ data }: { data: FeatureCollection }) => {
    const filteredFeatures = featureCollection(
      uniqBy(data.features, 'properties.mapUnitName'),
    );
    return getValidSections(
      filteredFeatures,
      (f) => f.properties?.description,
      mapUnitDescriptionSchema,
    ).flatMap((s) => {
      const { title: name, section = [] } = s.section[0]?.section[0] || {};
      const { section: minorComponents = [] } =
        section.find((sec) => sec.title === MINOR_COMPONENTS_TITLE) || {};
      return minorComponents.map((component: Section) => {
        const map = Object.fromEntries(
          component.variablelist.flatMap((vl) =>
            vl.varlistentry.map((vle) => [
              transformTerm(vle.term).accessor,
              vle.listitem.para,
            ]),
          ),
        );
        return {
          key: `${name}-${component.title}`,
          name,
          minorComponent: component.title,
          ...map,
        };
      });
    });
  },
};
